import React from 'react';
import { Modal, SectionCard } from './shared.jsx';

const BIConfigEditorModal = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  editingConfig,
  modulesMetadata,
  selectedModuleMetadata,
  draft,
  formError,
  adminOnlyFlags,
  supportedToggles,
  onModuleChange,
  onDiscriminantChange,
  onAdminOnlyFlagToggle,
  onBooleanToggleChange,
  onAdvancedModeToggle,
  onAdvancedJsonChange
}) => {
  const title = editingConfig
    ? `Edit ${selectedModuleMetadata?.label || editingConfig?.module}`
    : 'Create BI configuration';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="Prefer the guided form for common use cases. Advanced JSON remains available when needed."
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <SectionCard
              title="Scope"
              subtitle="Choose which module and which business target this configuration applies to."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Module
                  </label>
                  <select
                    value={draft.module}
                    onChange={onModuleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="" disabled>Select a module</option>
                    {modulesMetadata.map((moduleMetadata) => (
                      <option key={moduleMetadata.module} value={moduleMetadata.module}>
                        {moduleMetadata.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {selectedModuleMetadata?.discriminant_label || 'Discriminant'}
                  </label>
                  <input
                    type="text"
                    value={draft.discriminant_id}
                    onChange={onDiscriminantChange}
                    placeholder={selectedModuleMetadata?.discriminant_type || 'discriminant id'}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Visibility rules"
              subtitle="Configure what should remain visible only to admins and super admins."
            >
              {adminOnlyFlags.length > 0 ? (
                <div className="space-y-3">
                  {adminOnlyFlags.map((flag) => {
                    const checked = (draft.options?.admin_only || []).includes(flag.value);

                    return (
                      <label
                        key={flag.value}
                        className={`flex items-start gap-3 rounded-xl border p-4 transition-colors cursor-pointer ${
                          checked
                            ? 'border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onAdminOnlyFlagToggle(flag.value)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{flag.label}</p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Only admins and super admins will see this area.
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4 text-sm text-gray-500 dark:text-gray-400">
                  No guided options are available for this module yet. Use advanced JSON if needed.
                </div>
              )}
            </SectionCard>

            {supportedToggles.length > 0 && (
              <SectionCard
                title="Feature access"
                subtitle="Enable client-specific or premium capabilities for this module."
              >
                <div className="space-y-3">
                  {supportedToggles.map((toggle) => {
                    const checked = draft.options?.[toggle.key] === true;

                    return (
                      <label
                        key={toggle.key}
                        className={`flex items-start gap-3 rounded-xl border p-4 transition-colors cursor-pointer ${
                          checked
                            ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onBooleanToggleChange(toggle.key)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{toggle.label}</p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {toggle.description}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            <SectionCard
              title="Advanced JSON"
              subtitle="Optional raw editor for advanced cases or future module-specific options."
            >
              <div className="space-y-4">
                <label className="inline-flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={draft.advancedMode}
                    onChange={onAdvancedModeToggle}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Edit raw JSON manually
                </label>

                {draft.advancedMode ? (
                  <textarea
                    rows={10}
                    value={draft.advancedOptionsText}
                    onChange={onAdvancedJsonChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                  />
                ) : (
                  <pre className="rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {JSON.stringify(draft.options || {}, null, 2)}
                  </pre>
                )}
              </div>
            </SectionCard>
          </div>

          <div className="space-y-6">
            <SectionCard
              title="Module guide"
              subtitle="Hints and expected payload structure for the selected module."
            >
              {selectedModuleMetadata ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{selectedModuleMetadata.description}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Module id</p>
                      <p className="mt-1 font-mono text-sm text-gray-800 dark:text-gray-200">{selectedModuleMetadata.module}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Discriminant type</p>
                      <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">{selectedModuleMetadata.discriminant_label}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Example payload</p>
                    <pre className="mt-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-3 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                      {JSON.stringify(selectedModuleMetadata.example_options || {}, null, 2)}
                    </pre>
                  </div>

                  {selectedModuleMetadata.hints?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Hints</p>
                      <div className="mt-2 space-y-2">
                        {selectedModuleMetadata.hints.map((hint) => (
                          <div
                            key={hint}
                            className="rounded-xl bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700 dark:bg-blue-900/20 dark:border-blue-900/40 dark:text-blue-200"
                          >
                            {hint}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">Select a module to see its guidance.</p>
              )}
            </SectionCard>

            <SectionCard
              title="Current result"
              subtitle="Live preview of the payload that will be sent to the API."
            >
              <pre className="rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-3 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                {JSON.stringify(draft.options || {}, null, 2)}
              </pre>
            </SectionCard>
          </div>
        </div>

        {formError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
            {formError}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-60"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : editingConfig ? 'Save changes' : 'Create configuration'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BIConfigEditorModal;
